import React, { createContext, useState, useContext, useCallback, useMemo, useRef, ReactNode } from 'react';
import { releaseTestPlanExecution } from '../utils/executionGate';

/**
 * Execution state for a test cycle run that may continue in the background
 * when the user navigates away (e.g. to Collections). This context keeps
 * the run alive and allows the details page to show progress when the user returns.
 */
export interface ActiveTestCycleExecutionEnvironment {
  id: number;
  name: string;
  url: string;
}

export interface ActiveTestCycleExecution {
  cycleId: number;
  runId: number;
  executing: boolean;
  results: any[];
  /** Total test cases (for progress display "X of Y") */
  totalCount?: number;
  /** Collection currently executing a step (locks Collections UI for that collection). */
  executingCollectionId?: number | null;
  /** All collections in this test plan run (locked for the full execution). */
  executingCollectionIds?: number[];
  /**
   * Environment chosen at execution start. Stored so the details page can
   * re-hydrate its Environment dropdown after the user navigates away (e.g.
   * to Collections) and returns mid-run.
   */
  environment?: ActiveTestCycleExecutionEnvironment;
}

interface TestCycleExecutionContextType {
  /** Currently active execution (single run tracked globally). */
  activeExecution: ActiveTestCycleExecution | null;
  /** Set active execution state (start/update/clear). */
  setActiveExecution: React.Dispatch<React.SetStateAction<ActiveTestCycleExecution | null>>;
  /** Update only the results for the active execution (used during the run loop). */
  updateActiveResults: (cycleId: number, results: any[]) => void;
  /** Results recorded while the test plan page was unmounted. */
  getActiveResults: (cycleId: number) => any[];
  /** Mark active execution as finished. */
  finishActiveExecution: (cycleId: number) => void;
  /** Ref for abort signal - survives navigation so Stop works when user returns to page. */
  executionAbortedRef: React.MutableRefObject<boolean>;
  /** Request stop of the active execution (call when user clicks Stop). */
  requestStop: () => void;
}

const TestCycleExecutionContext = createContext<TestCycleExecutionContextType | undefined>(undefined);

export const TestCycleExecutionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeExecution, setActiveExecutionState] = useState<ActiveTestCycleExecution | null>(null);
  // Latest rows live on a ref. Writing them into React state re-renders every
  // subscriber — including Collections — on each step and stalls the run after
  // the user leaves the test plan page.
  const latestResultsRef = useRef<{ cycleId: number; results: any[] } | null>(null);

  const setActiveExecution = useCallback<React.Dispatch<React.SetStateAction<ActiveTestCycleExecution | null>>>((state) => {
    setActiveExecutionState((prev) => {
      const next = typeof state === 'function' ? state(prev) : state;
      if (prev?.executing && prev.cycleId > 0) {
        const stillSameRun =
          next != null && next.cycleId === prev.cycleId && next.executing === true;
        if (!stillSameRun) {
          releaseTestPlanExecution(prev.cycleId);
        }
      }
      if (next?.executing && (!prev?.executing || prev.cycleId !== next.cycleId)) {
        latestResultsRef.current = { cycleId: next.cycleId, results: next.results ?? [] };
      }
      return next;
    });
  }, []);

  const updateActiveResults = useCallback((cycleId: number, results: any[]) => {
    latestResultsRef.current = { cycleId, results };
  }, []);

  const getActiveResults = useCallback((cycleId: number): any[] => {
    const latest = latestResultsRef.current;
    if (!latest || latest.cycleId !== cycleId) return [];
    return latest.results;
  }, []);

  const finishActiveExecution = useCallback((cycleId: number) => {
    setActiveExecutionState((prev) => {
      if (!prev || prev.cycleId !== cycleId) return prev;
      releaseTestPlanExecution(cycleId);
      return { ...prev, executing: false };
    });
  }, []);

  const executionAbortedRef = useRef(false);
  const requestStop = useCallback(() => {
    executionAbortedRef.current = true;
    // Clear banner / executing flag even if the run loop is gone (e.g. navigated away) or stuck in a long await.
    setActiveExecutionState((prev) => {
      if (!prev || !prev.executing) return prev;
      releaseTestPlanExecution(prev.cycleId);
      return { ...prev, executing: false };
    });
  }, []);

  const value = useMemo<TestCycleExecutionContextType>(
    () => ({
      activeExecution,
      setActiveExecution,
      updateActiveResults,
      getActiveResults,
      finishActiveExecution,
      executionAbortedRef,
      requestStop,
    }),
    [activeExecution, setActiveExecution, updateActiveResults, getActiveResults, finishActiveExecution, requestStop],
  );

  // Keep the browser tab eligible to run while a test plan is in progress.
  // A hidden tab otherwise freezes timers and in-flight work, so switching to
  // Collections (or any other tab) looks like the run has stuck.
  React.useEffect(() => {
    const cycleId = activeExecution?.cycleId;
    const executing = activeExecution?.executing === true;
    if (!executing || cycleId == null || cycleId <= 0) return;
    if (typeof navigator === 'undefined' || !navigator.locks?.request) return;
    let release: (() => void) | undefined;
    const stayOpen = new Promise<void>((resolve) => {
      release = resolve;
    });
    void navigator.locks.request(`crud-expert-test-plan-${cycleId}`, { mode: 'shared' }, () => stayOpen);
    return () => {
      release?.();
    };
  }, [activeExecution?.executing, activeExecution?.cycleId]);

  return (
    <TestCycleExecutionContext.Provider value={value}>
      {children}
    </TestCycleExecutionContext.Provider>
  );
};

export function useTestCycleExecution(): TestCycleExecutionContextType {
  const context = useContext(TestCycleExecutionContext);
  if (context === undefined) {
    throw new Error('useTestCycleExecution must be used within a TestCycleExecutionProvider');
  }
  return context;
}
